import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaInstagram, FaFacebookF, FaPinterestP } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled.section`
  padding: 50px 0;
  background: #f8f6f3;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled(motion.h2)`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 400;
  color: #2c2c2c;
  margin-bottom: 20px;
`;

const Subtitle = styled(motion.p)`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #b8a398;
  font-weight: 500;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const ContactForm = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

/* Removed old input form styled-components (FormGroup, Label, Input, TextArea, SubmitButton) */

const ContactInfo = styled(motion.div)`
  padding: 20px 0;
`;

const InfoTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  color: #2c2c2c;
  margin-bottom: 30px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  
  svg {
    color: #b8a398;
    margin-right: 15px;
    font-size: 18px;
  }
`;

const InfoText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 18px;
  color: #5a5a5a;
  line-height: 1.6;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 40px;
`;

const SocialIcon = styled(motion.a)`
  width: 40px;
  height: 40px;
  border: 1px solid #d0d0d0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c2c2c;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2c2c2c;
    color: white;
    border-color: #2c2c2c;
  }
`;

const ContactSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });
  const { lang } = useLanguage();

  const formVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const infoVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // Old local form removed in favor of CRM iframe

  return (
    <SectionContainer ref={ref} id="contact">
      <ContentWrapper>
        <SectionHeader>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {lang === 'fr' ? 'Entrer en contact' : "Let's Connect"}
          </Subtitle>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {lang === 'fr' ? 'Me contacter' : 'Get In Touch'}
          </SectionTitle>
        </SectionHeader>
        <ContactGrid>
          <ContactForm
            variants={formVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <iframe
              title="Fotostudio contact form"
              src="https://www.fotostudio.io/lead_forms/12561"
              width="100%"
              height="750"
              frameBorder="0"
              allowFullScreen
              loading="lazy"
              style={{ border: 0 }}
            />
          </ContactForm>
          <ContactInfo
            variants={infoVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <InfoTitle>{lang === 'fr' ? 'Créons quelque chose de magique' : "Let's Create Magic Together"}</InfoTitle>
            <InfoItem>
              <FaPhone />
              <InfoText>+33 6 75 78 34 64</InfoText>
            </InfoItem>
            <InfoItem>
              <FaEnvelope />
              <InfoText>contact@linoludovic.fr</InfoText>
            </InfoItem>
            <InfoItem>
              <FaMapMarkerAlt />
              <InfoText>{lang === 'fr' ? 'Paris, France & International' : 'Paris, France & Worldwide'}</InfoText>
            </InfoItem>
            <SocialLinks>
              <SocialIcon
                href="https://www.instagram.com/linoludovic/?hl=fr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                title="Instagram"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaInstagram />
              </SocialIcon>
              <SocialIcon
                href="https://www.facebook.com/linoludovic/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                title="Facebook"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaFacebookF />
              </SocialIcon>
              <SocialIcon
                href="https://fr.pinterest.com/linoludovic/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pinterest"
                title="Pinterest"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaPinterestP />
              </SocialIcon>
            </SocialLinks>
          </ContactInfo>
        </ContactGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ContactSection;
